import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    House,
    FlaskConical,
    Radiation,
    Stethoscope,
    Pill,
    Building2,
    Calculator,
    FileText,
    Package,
    Settings as SettingsIcon,
    LogOut,
    CircleUserRound,
    PanelLeftClose,
    PanelLeftOpen,
    Boxes,
    ScrollText,
} from "lucide-react";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { AiChatWidget } from "./AiChatWidget";

const navGroups = [
    {
        label: null,
        items: [
            { to: "/", label: "Dashboard", icon: House, end: true, testid: "nav-dashboard" },
        ],
    },
    {
        label: "Data Master",
        items: [
            { to: "/master/lab", label: "Master Laboratorium", icon: FlaskConical, testid: "nav-master-lab" },
            { to: "/master/radiology", label: "Master Radiologi", icon: Radiation, testid: "nav-master-radiology" },
            { to: "/master/jasa", label: "Master Jasa Tindakan", icon: Stethoscope, testid: "nav-master-jasa" },
            { to: "/master/obat", label: "Master Obat & BMHP", icon: Pill, testid: "nav-master-obat" },
            { to: "/master/fasilitas", label: "Master Fasilitas", icon: Building2, testid: "nav-master-fasilitas" },
        ],
    },
    {
        label: "Data Unit Cost",
        items: [
            { to: "/unit-cost/lab", label: "Unit Cost Lab", icon: Calculator, testid: "nav-uc-lab" },
            { to: "/unit-cost/radiology", label: "Unit Cost Radiologi", icon: Calculator, testid: "nav-uc-radiology" },
            { to: "/unit-cost/jasa", label: "Unit Cost Jasa", icon: Calculator, testid: "nav-uc-jasa" },
            { to: "/unit-cost/fasilitas", label: "Unit Cost Fasilitas", icon: Calculator, testid: "nav-uc-fasilitas" },
        ],
    },
    {
        label: "Data Akuntansi",
        items: [
            { to: "/assets", label: "Inventaris Aset", icon: Boxes, testid: "nav-assets" },
            { to: "/audit-log", label: "Audit Log", icon: ScrollText, testid: "nav-audit-log" },
        ],
    },
    {
        label: null,
        items: [
            { to: "/mcu", label: "Kalkulator Paket Pemeriksaan", icon: FileText, testid: "nav-mcu" },
            { to: "/packages", label: "Paket Tersimpan", icon: Package, testid: "nav-packages" },
            { to: "/settings", label: "Pengaturan", icon: SettingsIcon, testid: "nav-settings" },
        ],
    },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [branding, setBranding] = useState(null);
    const [collapsed, setCollapsed] = useState(() => {
        return localStorage.getItem("mcu_sidebar_collapsed") === "1";
    });

    useEffect(() => {
        localStorage.setItem("mcu_sidebar_collapsed", collapsed ? "1" : "0");
    }, [collapsed]);

    useEffect(() => {
        axios
            .get(`${API_BASE}/public/branding`)
            .then((res) => setBranding(res.data))
            .catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-[#F7F9F6] text-[#1A362D] flex">
            <aside
                data-testid="sidebar"
                data-collapsed={collapsed}
                className={`bg-white border-r border-[#D1DCD8] flex flex-col shrink-0 transition-[width] duration-300 ease-in-out ${
                    collapsed ? "w-20" : "w-72"
                }`}
            >
                <div
                    className={`border-b border-[#D1DCD8] flex items-center ${
                        collapsed ? "px-3 py-4 justify-center" : "p-6 justify-between"
                    }`}
                >
                    {!collapsed && (
                        <div className="flex items-center gap-3 min-w-0">
                            {branding?.logo_url ? (
                                <img
                                    src={branding.logo_url}
                                    alt="Logo RS"
                                    data-testid="sidebar-logo"
                                    className="w-10 h-10 shrink-0 rounded-md object-contain bg-white border border-[#D1DCD8] p-0.5"
                                />
                            ) : (
                                <div className="w-10 h-10 shrink-0 rounded-md bg-[#245B4B] text-white flex items-center justify-center font-heading font-semibold">
                                    RS
                                </div>
                            )}
                            <div className="min-w-0">
                                <div className="font-heading font-semibold text-[#1A362D] leading-tight truncate">
                                    {branding?.name || "MCU Calculator"}
                                </div>
                                <div className="text-xs text-[#6A9C89] uppercase tracking-widest truncate">
                                    {branding?.tagline || "Rumah Sakit"}
                                </div>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => setCollapsed((c) => !c)}
                        data-testid="sidebar-toggle"
                        aria-label={collapsed ? "Buka sidebar" : "Kecilkan sidebar"}
                        title={collapsed ? "Buka sidebar" : "Kecilkan sidebar"}
                        className="p-2 rounded-md text-[#4A5E57] hover:bg-[#E9EFEA] hover:text-[#1A362D] transition-colors duration-200"
                    >
                        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                    </button>
                </div>

                <nav className="flex-1 p-3 overflow-y-auto">
                    {navGroups.map((group, gi) => (
                        <div key={gi} className={gi > 0 ? "mt-2" : ""}>
                            {group.label &&
                                (collapsed ? (
                                    <div className="my-2 border-t border-[#D1DCD8]" />
                                ) : (
                                    <div
                                        className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6A9C89]"
                                        data-testid={`nav-group-${group.label
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")}`}
                                    >
                                        {group.label}
                                    </div>
                                ))}
                            <div className="space-y-1">
                                {group.items.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.end}
                                        data-testid={item.testid}
                                        title={collapsed ? item.label : undefined}
                                        className={({ isActive }) =>
                                            `flex items-center rounded-md text-sm transition-colors duration-200 ${
                                                collapsed
                                                    ? "justify-center px-2 py-2.5"
                                                    : "gap-3 px-3 py-2.5"
                                            } ${
                                                isActive
                                                    ? "bg-[#245B4B] text-white font-medium"
                                                    : "text-[#4A5E57] hover:bg-[#E9EFEA] hover:text-[#1A362D]"
                                            }`
                                        }
                                    >
                                        <item.icon size={18} strokeWidth={1.75} className="shrink-0" />
                                        {!collapsed && <span className="truncate">{item.label}</span>}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className={`border-t border-[#D1DCD8] ${collapsed ? "p-3" : "p-4"}`}>
                    {!collapsed && (
                        <div className="flex items-center gap-3 mb-3">
                            <CircleUserRound size={30} className="text-[#245B4B]" strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                                <div
                                    className="text-sm font-medium truncate"
                                    data-testid="user-name"
                                >
                                    {user?.name || "Admin"}
                                </div>
                                <div className="text-xs text-[#6A9C89] truncate">
                                    {user?.email}
                                </div>
                            </div>
                        </div>
                    )}
                    <button
                        data-testid="logout-btn"
                        title="Keluar"
                        onClick={async () => {
                            await logout();
                            navigate("/login");
                        }}
                        className={`w-full flex items-center justify-center gap-2 rounded-md text-sm bg-[#E9EFEA] text-[#245B4B] hover:bg-[#C4DAD2] transition-colors duration-200 ${
                            collapsed ? "p-2" : "px-3 py-2"
                        }`}
                    >
                        <LogOut size={16} />
                        {!collapsed && <span>Keluar</span>}
                    </button>
                </div>
            </aside>

            <main className="flex-1 overflow-x-hidden">
                <div className="max-w-7xl mx-auto p-6 md:p-8 animate-fade-in-up">
                    <Outlet />
                </div>
            </main>

            <AiChatWidget />
        </div>
    );
}
